import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase/config';
import { OnboardingStep } from '../onboarding/onboarding.type';
import { UserRole } from '../user/user.type';

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  try {
    if (analytics) {
      const jobRoleId = sessionStorage.getItem('currentJobRoleId');
      const userRole = parameters?.user_role;
      const shouldIncludeJobRoleId = userRole !== UserRole.Founder && jobRoleId;

      const eventData = {
        ...parameters,
        source: process.env.NODE_ENV === 'development' ? 'local_onboarding' : 'prod_onboarding',
        app_type: 'web_onboarding',
        platform: 'web',
        session_id: sessionStorage.getItem('sessionId') || 'unknown',
        user_agent: navigator.userAgent,
        user_role: userRole,
        ...(shouldIncludeJobRoleId && { job_role_id: jobRoleId }),
      };

      logEvent(analytics, eventName, eventData);

    }
  } catch (error) {
  }
};

// Track when users visit the position page
export const trackPositionPageVisit = (referrer?: string) => {
  trackEvent('position_page_visit', {
    referrer: referrer || document.referrer || 'direct',
    timestamp: new Date().toISOString(),
  });
};

// Track when users click the apply button
export const trackApplyButtonClick = () => {
  trackEvent('apply_button_click', {
    timestamp: new Date().toISOString(),
  });
};

// Track onboarding step completion
export const trackOnboardingStepComplete = (step: OnboardingStep) => {
  trackEvent('onboarding_step_complete', {
    step,
    timestamp: new Date().toISOString(),
  });
};

// Track when users abandon onboarding (leave without completing)
export const trackOnboardingAbandoned = (step: OnboardingStep) => {
  trackEvent('onboarding_abandoned', {
    last_completed_step: step,
    timestamp: new Date().toISOString(),
  });
};

// Track full onboarding completion
export const trackOnboardingCompleted = () => {
  trackEvent('onboarding_completed', {
    timestamp: new Date().toISOString(),
  });
};

// Track personality test completion
export const trackPersonalityTestCompleted = () => {
  trackEvent('personality_test_completed', {
    timestamp: new Date().toISOString(),
  });
};

// Track when users reach the dashboard (full flow completion)
export const trackFullProcessCompleted = () => {
  trackEvent('full_process_completed', {
    timestamp: new Date().toISOString(),
  });
};

// Track page views for better user journey analysis
export const trackPageView = (pageName: string, additionalData?: Record<string, any>) => {
  trackEvent('page_view', {
    page_name: pageName,
    page_location: window.location.href,
    page_referrer: document.referrer,
    timestamp: new Date().toISOString(),
    ...additionalData,
  });
};

// Track user interactions (clicks, form submissions, etc.)
export const trackUserInteraction = (
  interactionType: string,
  elementId?: string,
  additionalData?: Record<string, any>,
) => {
  trackEvent('user_interaction', {
    interaction_type: interactionType,
    element_id: elementId,
    timestamp: new Date().toISOString(),
    ...additionalData,
  });
};

// Track form field interactions for onboarding progress
export const trackFormFieldInteraction = (
  fieldName: string,
  action: 'focus' | 'blur' | 'change' | 'error',
  step?: OnboardingStep,
  value?: string | number,
) => {
  trackEvent('form_field_interaction', {
    field_name: fieldName,
    action,
    onboarding_step: step,
    field_value_length: typeof value === 'string' ? value.length : undefined,
    timestamp: new Date().toISOString(),
  });
};

// Track onboarding step entry (when user first sees a step)
export const trackOnboardingStepEntry = (step: OnboardingStep) => {
  trackEvent('onboarding_step_entry', {
    step,
    timestamp: new Date().toISOString(),
  });
};

// Track errors for debugging
export const trackError = (
  errorType: string,
  errorMessage: string,
  location?: string,
  additionalData?: Record<string, any>,
) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    error_location: location,
    timestamp: new Date().toISOString(),
    ...additionalData,
  });
};

// Track session start for user journey analysis
export const trackSessionStart = () => {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('sessionId', sessionId);

  trackEvent('session_start', {
    session_id: sessionId,
    timestamp: new Date().toISOString(),
  });
};
